// oxlint-disable react/no-array-index-key -- index is the identity
import { styled } from "goober";
import {
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { queries } from "../../shared/queries";
import {
  isBuildExecutionKind,
  type ExecutionKind,
} from "../../shared/executions/executionKind";
import { useQuery } from "../hooks/useQuery";
import { useRunExecutions } from "../hooks/useStepExecutions";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { makeUrl, navigate } from "../lib/urls";
import { Blob } from "./Blob";
import { Dropdown } from "./Dropdown";
import { PressButton } from "./PressButton";
import { EmptyState } from "./EmptyState";
import { Icon } from "./Icon";
import { MetaSep } from "./MetaSeparator";
import { formatTime } from "./StepExecutions";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const PADDING_TOP = 8;
const BAR_BOTTOM_GAP = 12;

const PADDING_LEFT = 0;
const PADDING_RIGHT = 0;
const TICK_INSET = 24;
const FADE_X = 24;
const ROW_HEIGHT = 28;
const BAR_HEIGHT = 16;
const BAR_RADIUS = 8;
const MIN_BAR_WIDTH = 4;

/** SVG path for a rounded rect with independent left/right corner radii. */
function roundedRectPath(
  x: number,
  y: number,
  w: number,
  h: number,
  rLeft: number,
  rRight: number
): string {
  const rl = Math.min(rLeft, w / 2, h / 2);
  const rr = Math.min(rRight, w / 2, h / 2);
  return [
    `M${x + rl},${y}`,
    `H${x + w - rr}`,
    rr > 0 ? `A${rr},${rr} 0 0 1 ${x + w},${y + rr}` : `L${x + w},${y}`,
    `V${y + h - rr}`,
    rr > 0 ? `A${rr},${rr} 0 0 1 ${x + w - rr},${y + h}` : `L${x + w},${y + h}`,
    `H${x + rl}`,
    rl > 0 ? `A${rl},${rl} 0 0 1 ${x},${y + h - rl}` : `L${x},${y + h}`,
    `V${y + rl}`,
    rl > 0 ? `A${rl},${rl} 0 0 1 ${x + rl},${y}` : `L${x},${y}`,
    "Z",
  ].join(" ");
}

const Wrapper = styled("div")`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;

const HeaderRow = styled("div")`
  margin-top: 32px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0 32px 16px;
`;

const StatsRow = styled("div")`
  display: flex;
  gap: 32px;
`;

const StatItem = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 76px;
`;

const StatusBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px 1px 5px;
  border-radius: 10px;
  font-size: 9px;
  font-weight: 500;
  line-height: 14px;
  width: fit-content;
`;

const ChartArea = styled("div")`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 0 32px 32px;
  display: flex;
  flex-direction: column;
`;

const ChartContainer = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const FADE_Y = 16;

const BarsScroll = styled("div")`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  mask-image: linear-gradient(to bottom, transparent, black ${FADE_Y}px);
`;

const EmptyScroll = styled("div")`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 32px;
`;

const EmptyAligner = styled("div")`
  margin-top: auto;
  display: flex;
  justify-content: center;
`;

const InfoTooltip = styled("div")`
  position: fixed;
  background: var(--text-primary);
  color: var(--text-invert);
  border-radius: 8px;
  padding: 8px 10px;
  pointer-events: none;
  z-index: 10;
  font-size: 10px;
  line-height: 14px;
  white-space: nowrap;
  transform: translateY(-100%);
`;

const TooltipLabel = styled("span")`
  opacity: 0.6;
`;

type StepInfo = {
  canonicalId: string;
  name: string;
  color: string;
};

type Props = {
  runId: string | null;
  steps: ReadonlyArray<StepInfo>;
  selectedStepDir?: string | null;
  hoveredCanonicalId?: string | null;
  onStepHover?: (canonicalId: string | null) => void;
  onIdealHeight?: (height: number) => void;
  totalRunCount?: number;
  runs?: ReadonlyArray<{
    id: string;
    createdAt: number;
    ephemeral: boolean | null;
  }>;
  runStatuses?: ReadonlyMap<string, RunStatus>;
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return remainingSeconds > 0
    ? `${minutes}m ${remainingSeconds}s`
    : `${minutes}m`;
}

type WaterfallRow = {
  executionId: string;
  stepDir: string;
  stepName: string;
  stepColor: string;
  kind: ExecutionKind | null;
  status: "pending" | "success" | "error";
  startMs: number;
  queueMs: number;
  runMs: number;
  totalMs: number;
};

type HoveredBar = {
  row: WaterfallRow;
  index: number;
};

type RunStatus = "running" | "success" | "error";

function deriveRunStatus(
  executions: ReadonlyArray<{ status: "pending" | "success" | "error" }>
): RunStatus {
  if (executions.length === 0) return "running";
  if (executions.some((e) => e.status === "pending")) return "running";
  return executions.every((e) => e.status === "success") ? "success" : "error";
}

function runStatusLabel(status: RunStatus) {
  if (status === "success") return "Successful";
  if (status === "error") return "Failed";
  return "Running";
}

function runStatusColor(status: RunStatus) {
  if (status === "success") return "var(--green-600)";
  if (status === "error") return "var(--red-600)";
  return "var(--text-secondary)";
}

const NavPill = styled("div")`
  display: flex;
  align-items: center;
  height: 24px;
  background: var(--alpha4);
  border-radius: 12px;
`;

const NavDivider = styled("div")`
  width: 0.5px;
  height: 100%;
  background: var(--border-alpha);
  flex-shrink: 0;
`;

const NavButton = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  padding: 0;
  color: var(--text-secondary);
  &:hover {
    background: var(--alpha8);
  }
  &:disabled {
    opacity: 0.3;
    &:hover {
      background: transparent;
    }
  }
`;

const PickerPill = styled("button")`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  background: var(--alpha4);
  border-radius: 12px;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background: var(--alpha8);
  }
`;

export function RunWaterfall({
  runId,
  steps,
  selectedStepDir,
  hoveredCanonicalId,
  onStepHover,
  onIdealHeight,
  totalRunCount,
  runs,
  runStatuses,
}: Props) {
  const [run] = useQuery(
    queries.loadRunSummary({
      runId: runId ?? "",
    })
  );
  const { executions: runExecutions } = useRunExecutions(runId);
  const uid = useId();
  const fadeGradId = `${uid}-fade-grad`;
  const fadeMaskId = `${uid}-fade-mask`;
  const headerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [chartWidth, setChartWidth] = useState(0);

  // Re-run when `run` changes so the observer attaches after the chart
  // mounts (the early-return for null `run` skips the ChartArea element).
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- only need truthiness of run
  useLayoutEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setChartWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [run]);

  const stepMap = useMemo(
    () => new Map(steps.map((s) => [s.canonicalId, s])),
    [steps]
  );
  const [hovered, setHovered] = useState<HoveredBar | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const { rows, totalDuration } = useMemo(() => {
    if (!run) return { rows: [], totalDuration: 1 };
    const sorted = [...runExecutions].sort((a, b) => a.createdAt - b.createdAt);
    if (sorted.length === 0) return { rows: [], totalDuration: 1 };

    const now = Date.now();
    const earliestStart = sorted[0]!.createdAt;
    const latestEnd = Math.max(...sorted.map((e) => e.completedAt ?? now));
    const total = Math.max(latestEnd - earliestStart, 1);

    const mapped: WaterfallRow[] = sorted.map((exec) => {
      const step = stepMap.get(exec.stepDir!);
      const end = exec.completedAt ?? now;
      const startMs = Math.max(exec.createdAt - earliestStart, 0);
      const wallClock = Math.max(end - exec.createdAt, 0);
      const runTime =
        exec.duration != null && exec.completedAt != null
          ? exec.duration
          : wallClock;
      const queueTime = Math.max(wallClock - runTime, 0);
      return {
        executionId: exec.id,
        stepDir: exec.stepDir!,
        stepName: step?.name ?? exec.stepDir!,
        stepColor: step?.color ?? "gray",
        kind: exec.kind,
        status: exec.status,
        startMs,
        queueMs: queueTime,
        runMs: runTime,
        totalMs: wallClock,
      };
    });
    return { rows: mapped, totalDuration: total };
  }, [run, runExecutions, stepMap]);

  /* oxlint-disable react-hooks/exhaustive-deps -- report when row count changes */
  useLayoutEffect(() => {
    if (!onIdealHeight) return;
    const headerEl = headerRef.current;
    if (!headerEl) return;
    // header (including its margin-top) + bars + axis + ChartArea bottom padding + border
    const headerTotal = headerEl.offsetHeight + 32; // 32 = margin-top
    const chartTotal = barsHeight + AXIS_HEIGHT + 32; // 32 = ChartArea padding-bottom
    onIdealHeight(Math.ceil(headerTotal + chartTotal));
  }, [rows.length, onIdealHeight]);
  /* oxlint-enable react-hooks/exhaustive-deps */

  const runStatus = useMemo(() => {
    return run ? deriveRunStatus(runExecutions) : "running";
  }, [run, runExecutions]);

  const barAreaWidth = Math.max(chartWidth - PADDING_LEFT - PADDING_RIGHT, 0);
  const barsHeight =
    FADE_Y + PADDING_TOP + rows.length * ROW_HEIGHT + BAR_BOTTOM_GAP;
  const AXIS_HEIGHT = 34;

  const tickCount = 5;
  const ticks = useMemo(() => {
    const result: { offset: number; label: string }[] = [];
    for (let i = 0; i <= tickCount; i++) {
      const frac = i / tickCount;
      result.push({
        offset: frac,
        label: formatDuration(frac * totalDuration),
      });
    }
    return result;
  }, [totalDuration]);

  const handleBarHover = useCallback(
    (row: WaterfallRow, index: number) => {
      setHovered({ row, index });
      onStepHover?.(row.stepDir);
    },
    [onStepHover]
  );

  const handleBarLeave = useCallback(() => {
    setHovered(null);
    onStepHover?.(null);
  }, [onStepHover]);

  if (!run) {
    return (
      <Wrapper>
        <EmptyScroll>
          <EmptyAligner>
            <EmptyState
              iconName="runs"
              iconSize={24}
              maxWidth={220}
              title="No runs yet"
              description="Run this step to see its execution timeline here."
            />
          </EmptyAligner>
        </EmptyScroll>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <HeaderRow ref={headerRef}>
        <StatsRow>
          {totalRunCount != null && (
            <StatItem>
              <Text variant="text-small" color="var(--text-secondary)">
                Runs
              </Text>
              <Text variant="text-regular-md" color="var(--text-primary)">
                {totalRunCount}
              </Text>
            </StatItem>
          )}
          <StatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Run
            </Text>
            <Text variant="text-regular-md" color="var(--text-primary)">
              {new Date(run.createdAt).toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
              })}
            </Text>
            <StatusBadge
              style={
                runStatus === "success"
                  ? {
                      color: "var(--green-600)",
                      background:
                        "color-mix(in srgb, var(--green-600) 12%, transparent)",
                    }
                  : runStatus === "error"
                    ? {
                        color: "var(--red-600)",
                        background:
                          "color-mix(in srgb, var(--red-600) 12%, transparent)",
                      }
                    : {
                        color: "var(--text-secondary)",
                        background: "var(--alpha4)",
                      }
              }
            >
              <Icon
                name={
                  runStatus === "success"
                    ? "check"
                    : runStatus === "error"
                      ? "x"
                      : "clock"
                }
                size={10}
                color={
                  runStatus === "success"
                    ? "var(--green-600)"
                    : runStatus === "error"
                      ? "var(--red-600)"
                      : "var(--text-secondary)"
                }
              />
              {runStatus === "success"
                ? "Succeeded"
                : runStatus === "error"
                  ? "Failed"
                  : "Running"}
            </StatusBadge>
          </StatItem>
          <StatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Duration
            </Text>
            <Text variant="text-regular-md" color="var(--text-primary)">
              {formatDuration(totalDuration)}
            </Text>
          </StatItem>
          <StatItem>
            <Text variant="text-small" color="var(--text-secondary)">
              Steps
            </Text>
            <Text variant="text-regular-md" color="var(--text-primary)">
              {rows.length}
            </Text>
          </StatItem>
        </StatsRow>
        {runs && runs.length > 1 ? (
          <Dropdown
            align="bottom"
            minWidth={280}
            opener={
              <PickerPill type="button">
                <Icon name={run.ephemeral ? "solo-run" : "run"} size={12} />
                <Text variant="text-small-md" color="var(--text-secondary)">
                  {formatTime(run.createdAt)}
                </Text>
                <Icon name="chevron-down" size={12} />
              </PickerPill>
            }
            sections={[
              {
                items: runs.map((r) => {
                  const status = runStatuses?.get(r.id) ?? "running";
                  return {
                    type: "link",
                    key: r.id,
                    selected: r.id === runId,
                    left: (
                      <Icon name={r.ephemeral ? "solo-run" : "run"} size={12} />
                    ),
                    label: formatTime(r.createdAt),
                    subtitle: (
                      <Text variant="text-small" ellipsis>
                        <span style={{ color: runStatusColor(status) }}>
                          {runStatusLabel(status)}
                        </span>
                        <span style={{ color: "var(--text-secondary)" }}>
                          <MetaSep />
                          {formatRelativeTime(r.createdAt)}
                        </span>
                      </Text>
                    ),
                    right:
                      r.id === runId ? (
                        <Icon
                          name="check"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      ) : null,
                    to: makeUrl(null, { run: r.id }),
                  };
                }),
              },
            ]}
          />
        ) : (
          rows.length > 1 &&
          (() => {
            const currentIndex = selectedStepDir
              ? rows.findIndex((r) => r.stepDir === selectedStepDir)
              : -1;
            const hasPrev = currentIndex > 0;
            const hasNext = currentIndex >= 0 && currentIndex < rows.length - 1;
            const goTo = (index: number) => {
              const row = rows[index]!;
              navigate(null, {
                step: row.stepDir,
                execution: row.executionId,
              });
            };
            return (
              <NavPill>
                <Tooltip content="Previous step">
                  <NavButton
                    style={{ borderRadius: "12px 0 0 12px" }}
                    disabled={!hasPrev}
                    onPress={() => goTo(currentIndex - 1)}
                  >
                    <Icon name="chevron-left" size={12} />
                  </NavButton>
                </Tooltip>
                <NavDivider />
                <Tooltip content="Next step">
                  <NavButton
                    style={{ borderRadius: "0 12px 12px 0" }}
                    disabled={!hasNext}
                    onPress={() => goTo(currentIndex + 1)}
                  >
                    <Icon name="chevron-right" size={12} />
                  </NavButton>
                </Tooltip>
              </NavPill>
            );
          })()
        )}
      </HeaderRow>

      <ChartArea ref={chartRef}>
        <ChartContainer>
          <BarsScroll>
            <svg
              ref={svgRef}
              width={chartWidth}
              height={barsHeight}
              style={{ flexShrink: 0, marginTop: "auto" }}
            >
              {rows.map((row, i) => {
                const y = FADE_Y + PADDING_TOP + i * ROW_HEIGHT;
                const barY = y + (ROW_HEIGHT - BAR_HEIGHT) / 2;
                const barX =
                  PADDING_LEFT + (row.startMs / totalDuration) * barAreaWidth;
                const queueWidth = (row.queueMs / totalDuration) * barAreaWidth;
                const runWidth = Math.max(
                  (row.runMs / totalDuration) * barAreaWidth,
                  MIN_BAR_WIDTH
                );
                const fullWidth = queueWidth + runWidth;
                const stepColor = `var(--${row.stepColor}-600)`;
                const hasQueue = row.queueMs > 0 && queueWidth >= 2;
                const isHovered =
                  hovered?.index === i || hoveredCanonicalId === row.stepDir;
                const isSelected = selectedStepDir === row.stepDir;

                return (
                  <g key={`${row.stepDir}-${row.kind}-${i}`}>
                    {hasQueue && (
                      <path
                        d={roundedRectPath(
                          barX,
                          barY,
                          queueWidth,
                          BAR_HEIGHT,
                          BAR_RADIUS,
                          0
                        )}
                        fill={`color-mix(in srgb, ${stepColor} ${isSelected ? 40 : isHovered ? 15 : 8}%, var(--surface-secondary))`}
                        style={{ transition: "fill 120ms ease" }}
                      />
                    )}

                    <path
                      d={roundedRectPath(
                        barX + queueWidth,
                        barY,
                        runWidth,
                        BAR_HEIGHT,
                        hasQueue ? 0 : BAR_RADIUS,
                        BAR_RADIUS
                      )}
                      fill={
                        isSelected
                          ? stepColor
                          : `color-mix(in srgb, ${stepColor} ${isHovered ? 50 : 35}%, var(--surface-secondary))`
                      }
                      style={{ transition: "fill 120ms ease" }}
                    />

                    {row.status === "error" && (
                      <g>
                        <circle
                          cx={barX + fullWidth + 10}
                          cy={barY + BAR_HEIGHT / 2}
                          r={6}
                          fill="color-mix(in srgb, var(--red-600) 15%, transparent)"
                        />
                        <line
                          x1={barX + fullWidth + 10 - 2.5}
                          y1={barY + BAR_HEIGHT / 2 - 2.5}
                          x2={barX + fullWidth + 10 + 2.5}
                          y2={barY + BAR_HEIGHT / 2 + 2.5}
                          stroke="var(--red-600)"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <line
                          x1={barX + fullWidth + 10 + 2.5}
                          y1={barY + BAR_HEIGHT / 2 - 2.5}
                          x2={barX + fullWidth + 10 - 2.5}
                          y2={barY + BAR_HEIGHT / 2 + 2.5}
                          stroke="var(--red-600)"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </g>
                    )}

                    {row.status === "pending" && (
                      <rect
                        x={barX}
                        y={barY}
                        width={fullWidth}
                        height={BAR_HEIGHT}
                        rx={BAR_RADIUS}
                        ry={BAR_RADIUS}
                        fill={`color-mix(in srgb, ${stepColor} 20%, var(--surface-secondary))`}
                        style={{ pointerEvents: "none" }}
                      />
                    )}
                  </g>
                );
              })}

              {rows.map((row, i) => {
                const hitX =
                  PADDING_LEFT + (row.startMs / totalDuration) * barAreaWidth;
                const qw = (row.queueMs / totalDuration) * barAreaWidth;
                const rw = Math.max(
                  (row.runMs / totalDuration) * barAreaWidth,
                  MIN_BAR_WIDTH
                );
                return (
                  <rect
                    key={`hit-${row.stepDir}-${row.kind}-${i}`}
                    x={hitX}
                    y={FADE_Y + PADDING_TOP + i * ROW_HEIGHT}
                    width={qw + rw}
                    height={ROW_HEIGHT}
                    fill="transparent"
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => handleBarHover(row, i)}
                    onMouseLeave={handleBarLeave}
                    onClick={() =>
                      navigate(null, {
                        step: row.stepDir,
                        execution: row.executionId,
                      })
                    }
                  />
                );
              })}
            </svg>
          </BarsScroll>
          <svg
            width={chartWidth}
            height={AXIS_HEIGHT}
            style={{ flexShrink: 0 }}
          >
            <defs>
              <linearGradient id={fadeGradId} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="white" stopOpacity="0" />
                <stop
                  offset={
                    chartWidth > 0 ? `${(FADE_X / chartWidth) * 100}%` : "0%"
                  }
                  stopColor="white"
                  stopOpacity="1"
                />
                <stop
                  offset={
                    chartWidth > 0
                      ? `${((chartWidth - FADE_X) / chartWidth) * 100}%`
                      : "100%"
                  }
                  stopColor="white"
                  stopOpacity="1"
                />
                <stop offset="100%" stopColor="white" stopOpacity="0" />
              </linearGradient>
              <mask id={fadeMaskId}>
                <rect
                  x="0"
                  y="0"
                  width={chartWidth}
                  height={AXIS_HEIGHT}
                  fill={`url(#${fadeGradId})`}
                />
              </mask>
            </defs>
            <g mask={`url(#${fadeMaskId})`}>
              {rows.length > 0 && (
                <line
                  x1={0}
                  y1={1}
                  x2={chartWidth}
                  y2={1}
                  stroke="var(--gray-400)"
                  strokeWidth="2"
                />
              )}

              {ticks.map((tick) => {
                const tickAreaWidth = Math.max(chartWidth - TICK_INSET * 2, 0);
                const x = TICK_INSET + tick.offset * tickAreaWidth;
                return (
                  <text
                    key={tick.offset}
                    x={x}
                    y={AXIS_HEIGHT - 8}
                    textAnchor="middle"
                    fill="var(--text-secondary)"
                    fontSize="10"
                    fontFamily="var(--font-mono)"
                    fontWeight="350"
                  >
                    {tick.label}
                  </text>
                );
              })}
            </g>
          </svg>
          {(() => {
            const resolveTarget = (dir: string | null | undefined) => {
              if (!dir) return null;
              const idx = rows.findIndex((r) => r.stepDir === dir);
              return idx >= 0 ? { row: rows[idx]!, index: idx } : null;
            };
            const tooltipTarget = hovered
              ? { row: hovered.row, index: hovered.index }
              : resolveTarget(hoveredCanonicalId);
            if (!tooltipTarget) return null;
            const row = tooltipTarget.row;
            const svgRect = svgRef.current?.getBoundingClientRect();
            if (!svgRect) return null;
            const tooltipWidth = tooltipRef.current?.offsetWidth ?? 0;
            const baseLeft =
              svgRect.left +
              PADDING_LEFT +
              (row.startMs / totalDuration) * barAreaWidth;
            const flipped = baseLeft + tooltipWidth > window.innerWidth - 8;
            const fixedLeft = flipped ? baseLeft - tooltipWidth - 16 : baseLeft;
            const fixedTop =
              svgRect.top +
              FADE_Y +
              PADDING_TOP +
              tooltipTarget.index * ROW_HEIGHT +
              (ROW_HEIGHT - BAR_HEIGHT) / 2 -
              8;
            return (
              <InfoTooltip
                ref={tooltipRef}
                style={{
                  left: fixedLeft,
                  top: fixedTop,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontWeight: 500,
                    marginBottom: 2,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "stretch",
                      height: 10,
                      width: 3,
                      flexShrink: 0,
                    }}
                  >
                    <Blob colors={[`var(--${row.stepColor}-600)`]} width={3} />
                  </div>
                  {row.stepName}
                  {row.kind && isBuildExecutionKind(row.kind) ? " (build)" : ""}
                </div>
                {row.queueMs > 0 && (
                  <div>
                    <TooltipLabel>Queue </TooltipLabel>
                    {formatDuration(row.queueMs)}
                  </div>
                )}
                <div>
                  <TooltipLabel>Run </TooltipLabel>
                  {formatDuration(row.runMs)}
                </div>
                <div>
                  <TooltipLabel>Total </TooltipLabel>
                  {formatDuration(row.totalMs)}
                </div>
              </InfoTooltip>
            );
          })()}
        </ChartContainer>
      </ChartArea>
    </Wrapper>
  );
}
